import { z } from 'zod';

/**
 * SIL-2409 (PR 2): owner-facing DTOs of the public buyer passport - the QR token list/issue/revoke routes and the per-asset
 * publication toggle. Design: docs/plans/sil-2409-public-passport-conditions.md (C1). No DTO here ever carries a token hash, an org
 * id or another asset's id; the raw token appears exactly once, in the issue response.
 */

export const PASSPORT_QR_TOKEN_SOURCES = ['bench_label', 'owner_share'] as const;
export const passportQrTokenSourceSchema = z.enum(PASSPORT_QR_TOKEN_SOURCES);
export type PassportQrTokenSource = z.infer<typeof passportQrTokenSourceSchema>;

/** One token of an asset, metadata only. `issuedBeforeTransfer`: the token was forwarded to the caller's org by a sale (O2). */
export const passportQrTokenSchema = z
  .object({
    id: z.string().uuid(),
    source: passportQrTokenSourceSchema,
    createdAt: z.string(),
    revokedAt: z.string().nullable(),
    issuedBeforeTransfer: z.boolean(),
  })
  .strict();
export type PassportQrTokenMeta = z.infer<typeof passportQrTokenSchema>;

export const passportQrTokenListSchema = z.object({ tokens: z.array(passportQrTokenSchema) }).strict();
export type PassportQrTokenList = z.infer<typeof passportQrTokenListSchema>;

/** Response of issuing an owner_share token. `token` and `url` are shown once and never stored in plaintext. */
export const passportQrTokenIssuedSchema = z
  .object({ tokenId: z.string().uuid(), token: z.string(), url: z.string() })
  .strict();
export type PassportQrTokenIssued = z.infer<typeof passportQrTokenIssuedSchema>;

export const passportQrTokenRevokedSchema = z.object({ revoked: z.number().int().nonnegative() }).strict();
export type PassportQrTokenRevoked = z.infer<typeof passportQrTokenRevokedSchema>;

/** Who changed the publication state last. `id` is the user id for `client_user` only (ops and system labels are not exposed). */
export const passportPublicationActorSchema = z
  .object({ kind: z.enum(['client_user', 'ops_admin', 'system']), id: z.string().nullable() })
  .strict();

/**
 * SIL-2439: why a publish would be refused right now, for the owner UI. ADMIN-ONLY (members get `null`: they cannot act on it, and the event
 * table is org-scoped while asset rows are team-scoped for members). Built from the caller's own client session, so RLS still applies.
 * No serials: the model name and class are enough to find the part. `parentPublished` / `parentTakenDown` apply to a component,
 * `publishedComponents` to a system. A system or component is never public together with its counterpart (refusal in the database).
 */
export const passportPublicationBlockersSchema = z
  .object({
    /** The system this component is installed in is marked public: the component cannot be published on its own. */
    parentPublished: z.boolean(),
    /** Operations took the system this component is installed in down: the component stays private too. */
    parentTakenDown: z.boolean(),
    /** Components of this system that are public on their own: the system cannot be published until they are private. */
    publishedComponents: z.array(z.object({ assetId: z.string().uuid(), modelName: z.string().nullable(), assetClassId: z.string() }).strict()),
  })
  .strict();
export type PassportPublicationBlockers = z.infer<typeof passportPublicationBlockersSchema>;

export const passportPublicationSchema = z
  .object({
    published: z.boolean(),
    changedAt: z.string().nullable(),
    changedBy: passportPublicationActorSchema.nullable(),
    /** Operations took the passport down: the owner toggle is locked until operations clears it. */
    takenDown: z.boolean(),
    /** Admin-only (null for a member); see passportPublicationBlockersSchema. */
    blockers: passportPublicationBlockersSchema.nullable(),
  })
  .strict();
export type PassportPublication = z.infer<typeof passportPublicationSchema>;

/** `listing_create` is written by the create-listing route only; an owner PUT supplies `owner_toggle` (default) or `listing_end`. */
export const setPassportPublicationInputSchema = z
  .object({ published: z.boolean(), reason: z.enum(['owner_toggle', 'listing_end']).optional() })
  .strict();
export type SetPassportPublicationInput = z.infer<typeof setPassportPublicationInputSchema>;
